import { useEffect, useState } from 'react';
import { FEATURE_FLAGS } from '@/config/apiConfig';
import { setSoundEnabled } from '@/utils/soundUtils';

/**
 * Sound preference — env kill-switch + user toggle (localStorage).
 * Effective ON = VITE_ENABLE_SOUND_EFFECTS allowed AND user preference ON (default true).
 */

const STORAGE_KEY = 'kiosk.soundEffectsEnabled';

type PreferenceListener = (enabled: boolean) => void;
const listeners = new Set<PreferenceListener>();

export function isSoundEffectsAllowedByEnv(): boolean {
  return FEATURE_FLAGS.ENABLE_SOUND_EFFECTS;
}

export function getSoundEffectsPreference(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export function getEffectiveSoundEffectsEnabled(): boolean {
  return isSoundEffectsAllowedByEnv() && getSoundEffectsPreference();
}

export function applySoundEffectsEnabled(): void {
  setSoundEnabled(getEffectiveSoundEffectsEnabled());
}

export function setSoundEffectsPreference(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    /* private mode */
  }
  applySoundEffectsEnabled();
  listeners.forEach((l) => l(getSoundEffectsPreference()));
}

function subscribe(listener: PreferenceListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook: [userPreference, setPreference, { envAllowed }] */
export function useSoundEffectsPreference(): [
  boolean,
  (enabled: boolean) => void,
  { envAllowed: boolean },
] {
  const [enabled, setEnabled] = useState<boolean>(getSoundEffectsPreference);
  const envAllowed = isSoundEffectsAllowedByEnv();

  useEffect(() => subscribe(setEnabled), []);

  return [enabled, setSoundEffectsPreference, { envAllowed }];
}

if (typeof window !== 'undefined') {
  applySoundEffectsEnabled();
}
